import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  FaChevronDown,
  FaChevronUp,
  FaDownload,
  FaFilePdf,
} from 'react-icons/fa';
import { getNotifications } from '../../../api/services/systemService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Title = styled.h1`
  width: 100%;
  font-size: 24px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NotificationItem = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const NotificationHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f9f9f9;
  }
`;

const NotificationHeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NotificationTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.primaryText};
  margin: 0;
`;

const NotificationDate = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.colors.secondaryText};
  opacity: 0.7;
`;

const ExpandIcon = styled.div`
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 14px;
  margin-left: 1rem;
`;

const NotificationBody = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: ${(props) => (props.isExpanded ? 'block' : 'none')};
  border-top: 1px solid #f0f0f0;
  padding-top: 1rem;
`;

const NotificationContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ImageContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
`;

const NotificationImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const NotificationText = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  line-height: 1.6;
  margin: 0;

  p {
    margin: 0 0 0.5rem 0;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  a {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const Attachments = styled.div`
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
`;

const AttachmentsTitle = styled.h4`
  margin: 0 0 0.75rem;
  color: ${(props) => props.theme.colors.primaryText};
  font-size: 14px;
  font-weight: 600;
`;

const AttachmentList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  color: ${(props) => props.theme.colors.secondaryText};
  text-decoration: none;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  &:hover,
  &:focus-visible {
    border-color: ${(props) => props.theme.colors.primaryBlue};
    background: ${(props) => props.theme.colors.subBackground};
  }
`;

const PdfIcon = styled(FaFilePdf)`
  flex: 0 0 auto;
  color: #d93025;
  font-size: 20px;
`;

const AttachmentName = styled.span`
  min-width: 0;
  flex: 1;
  overflow-wrap: anywhere;
  font-size: 14px;
`;

const DownloadIcon = styled(FaDownload)`
  flex: 0 0 auto;
  color: ${(props) => props.theme.colors.primaryBlue};
  font-size: 14px;
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  background: ${(props) => props.theme.colors.subBackground};
  border-radius: 8px;
  text-align: center;
  color: ${(props) => props.theme.colors.secondaryText};
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  background: ${(props) => props.theme.colors.subBackground};
  border-radius: 8px;
  text-align: center;
  color: #e74c3c;
`;

const EmptyMessage = styled.div`
  padding: 2rem;
  background: ${(props) => props.theme.colors.subBackground};
  border-radius: 8px;
  text-align: center;
  color: ${(props) => props.theme.colors.secondaryText};
`;

const NotificationsView = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpand = (notificationId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getNotifications();

        if (result.is_success && result.notificationList) {
          // Sort by issueDt (most recent date/time on top)
          const sortedNotifications = result.notificationList.sort((a, b) => {
            return new Date(b.issueDt) - new Date(a.issueDt);
          });
          setNotifications(sortedNotifications);
        } else {
          setNotifications([]);
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getImageUrl = (notification) => {
    const imagePath = notification.filepath1;
    if (!imagePath) return null;
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    return `${process.env.REACT_APP_FILE_UPLOAD_URL || ''}${imagePath}`;
  };

  const getFileUrl = (filepath) => {
    if (/^https?:\/\//i.test(filepath)) return filepath;
    return `${process.env.REACT_APP_FILE_UPLOAD_URL || ''}${filepath}`;
  };

  const getFileName = (file) => {
    if (file.descr?.trim()) return file.descr.trim();

    const pathParts = file.filepath1.split('/');
    const fileName = pathParts[pathParts.length - 1];
    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  };

  if (loading) {
    return (
      <Container>
        <Title>Notifications</Title>
        <LoadingMessage>Loading notifications...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Title>Notifications</Title>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  if (notifications.length === 0) {
    return (
      <Container>
        <Title>Notifications</Title>
        <EmptyMessage>No notifications available at this time.</EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Notifications</Title>
      <NotificationsList>
        {notifications.map((notification) => {
          const isExpanded = expandedItems.has(notification.idNotification);
          return (
            <NotificationItem key={notification.idNotification}>
              <NotificationHeader
                onClick={() => toggleExpand(notification.idNotification)}
              >
                <NotificationHeaderContent>
                  <NotificationTitle>{notification.title}</NotificationTitle>
                  {notification.issueDt && (
                    <NotificationDate>
                      {formatDate(notification.issueDt)}
                    </NotificationDate>
                  )}
                </NotificationHeaderContent>
                <ExpandIcon>
                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </ExpandIcon>
              </NotificationHeader>
              <NotificationBody isExpanded={isExpanded}>
                <NotificationContent>
                  {notification.filepath1 && (
                    <ImageContainer>
                      <NotificationImage
                        src={getImageUrl(notification)}
                        alt={notification.title}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </ImageContainer>
                  )}
                  <NotificationText
                    dangerouslySetInnerHTML={{ __html: notification.content }}
                  />
                  {notification.files?.length > 0 && (
                    <Attachments>
                      <AttachmentsTitle>Attached documents</AttachmentsTitle>
                      <AttachmentList>
                        {notification.files.map((file) => {
                          const fileName = getFileName(file);
                          return (
                            <li key={file.idFile}>
                              <AttachmentLink
                                href={getFileUrl(file.filepath1)}
                                download={fileName}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={`Download ${fileName}`}
                              >
                                <PdfIcon aria-hidden='true' />
                                <AttachmentName>{fileName}</AttachmentName>
                                <DownloadIcon aria-hidden='true' />
                              </AttachmentLink>
                            </li>
                          );
                        })}
                      </AttachmentList>
                    </Attachments>
                  )}
                </NotificationContent>
              </NotificationBody>
            </NotificationItem>
          );
        })}
      </NotificationsList>
    </Container>
  );
};

export default NotificationsView;
